import {
	FinalizePartnershipRequest,
	PAGES,
	PositionInTheCompanyChoice,
	howDidYouKnowDuneChoice,
} from "@dune/types";
import {
	Button,
	Card,
	ControlledCheckbox,
	ControlledDateInput,
	ControlledDropdown,
	ControlledInput,
	ControlledPhoneInput,
	DataTable,
	Separator,
	cn,
	useSidebar,
} from "@dune/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "@untitled-ui/icons-react";
import { FileWarning } from "lucide-react";
import { useEffect } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	useFinalSaveAnalysePartneship,
	useSaveAnalysePartneship,
} from "../../../api/mutations/societies.mutations";
import { societiesQueryKeys } from "../../../api/query-keys/societies.query-keys";
import { FileWithMetadataPreviewWrapper } from "../../../component/layout/FileWithMetadataPreviewWrapper";
import { useDocumentStore } from "../../../stores/document.store";
import { useCurrentPanel, usePanelStore } from "../../../stores/panel.store";
import { DocumentSection } from "./components/DocumentSection";
import { FloatingActionBar } from "./components/FloatingActionBar";
import { SocietySection } from "./components/SocietySection";
import { UserSection } from "./components/UserSection";
import { ValidationStatus } from "./components/ValidationStatus";
import { accessibleProductsColumns } from "./productsColumns";
import {
	PartnershipRequestAnalysisFormData,
	usePartnershipRequestAnalysis,
} from "./usePartnershipRequestAnalysis";

interface PartnershipRequestAnalysisContentProps {
	id: string;
}

export const PartnershipRequestAnalysisContent = ({
	id,
}: PartnershipRequestAnalysisContentProps) => {
	const navigate = useNavigate();
	const { setOpen } = useSidebar();
	const currentPanel = useCurrentPanel();
	const { clearPanel } = usePanelStore();
	const queryClient = useQueryClient();
	const { fileId } = useDocumentStore();

	const isPanelOpen = !!currentPanel || !!fileId;

	useEffect(() => {
		setOpen(false);
		clearPanel();

		if (id) {
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerDetails(id),
			});
		}
	}, [setOpen, clearPanel, queryClient, id]);

	const form = usePartnershipRequestAnalysis(id);
	const { control, handleSubmit, formState, watch, getValues, setValue } = form;
	const { errors, isValid } = formState;
	const watchedCompanyDocs =
		useWatch({ control, name: "companyDocuments" }) || [];
	const watchedUserDocs = useWatch({ control, name: "userDocuments" }) || [];
	const watchedLcbftDocs = useWatch({ control, name: "lcbftDocuments" }) || [];
	const watchedRcDocs = useWatch({ control, name: "rcDocuments" }) || [];
	const watchedBankDocs = useWatch({ control, name: "bankDocuments" }) || [];

	const allWatchedDocs = [
		...watchedCompanyDocs,
		...watchedUserDocs,
		...watchedLcbftDocs,
		...watchedRcDocs,
		...watchedBankDocs,
	];

	// Calculate document statistics
	const totalDocuments = allWatchedDocs.length;
	const analyzedDocuments = allWatchedDocs.filter(
		(doc) => doc.file.humanProcessingStatus !== "TO_BE_PROCESSED",
	).length;
	const compliantDocuments = allWatchedDocs.filter(
		(doc) => doc.file.humanProcessingStatus === "COMPLIANT",
	).length;

	const hasCompliantInCategory = (docs: typeof watchedCompanyDocs) =>
		docs.length === 0 ||
		docs.some((doc) => doc.file.humanProcessingStatus === "COMPLIANT");

	const canValidateByCategory =
		hasCompliantInCategory(watchedCompanyDocs) &&
		hasCompliantInCategory(watchedUserDocs) &&
		hasCompliantInCategory(watchedRcDocs) &&
		hasCompliantInCategory(watchedBankDocs) &&
		hasCompliantInCategory(watchedLcbftDocs);

	const { mutate: saveMutate, isPending: saveIsPending } =
		useSaveAnalysePartneship();
	const { mutate: finalMutate, isPending: finalIsPending } =
		useFinalSaveAnalysePartneship();

	const buildFinalizeRequestData = (
		data: PartnershipRequestAnalysisFormData & {
			userId: number;
		},
	): FinalizePartnershipRequest => {
		const allFiles = [
			...(data.companyDocuments || []),
			...(data.userDocuments || []),
			...(data.lcbftDocuments || []),
			...(data.rcDocuments || []),
			...(data.bankDocuments || []),
		];

		const files = allFiles.map((doc) => ({
			file: doc.file,
			type: doc.type,
			status: doc.file.humanProcessingStatus,
		}));

		return {
			user: {
				id: data.userId,
				lastname: data.lastname,
				firstname: data.firstname,
				email: data.email,
				position: data.position,
				landlinePhone: data.landlinePhone || undefined,
				phone: data.phone,
				isSignatory: data.isSignatory === "true",
				howDidYouKnowDune: data.howDidYouKnowDune,
				hasSubscribedToNewsletter: data.newsletter ?? false,
				...(data.lastnameSignatory && {
					lastnameSignatory: data.lastnameSignatory,
				}),
				...(data.firstnameSignatory && {
					firstnameSignatory: data.firstnameSignatory,
				}),
				...(data.emailSignatory && { emailSignatory: data.emailSignatory }),
				...(data.positionSignatory && {
					positionSignatory: data.positionSignatory,
				}),
				...(data.landlinePhoneSignatory && {
					landlinePhoneSignatory: data.landlinePhoneSignatory,
				}),
				...(data.phoneSignatory && { phoneSignatory: data.phoneSignatory }),
			},
			society: {
				id: data.societyId,
				oriasNumber: data.oriasNumber || "",
				siren: data.siren || "",
				denomination: data.denomination || "",
				headquartersAddress: data.headquartersAddress || {
					streetNumber: "",
					streetName: "",
					postalCode: "",
					city: "",
				},
				categories:
					data.categories?.map((cat) => ({
						category: cat.category,
						status: cat.status,
						registrationDate: cat.registrationDate,
						categoryDeletionDate: cat.categoryDeletionDate,
						fundCollection: cat.fundCollection,
						mandants: cat.mandants,
					})) ?? [],
				accessibleProducts: data.accessibleProducts.map((product) => ({
					product: product.name,
					authorized: product.authorized === "true",
					commission: product.commission,
					minCommission: product.minCommission,
					maxCommission: product.maxCommission,
					isReunionDepartmentAuthorized: product.isReunionDepartmentAuthorized,
					binderId: product.binderId || undefined,
					warrantyType: product.warrantyType || undefined,
					gfaProduct: product.gfaProduct || false,
				})),
			},
			banking: {
				iban: data.iban,
				bic: data.bic,
			},
			insurance: {
				insurer: data.insurer,
				validityStartDate: data.validityStartDate
					? new Date(data.validityStartDate.toString())
					: undefined,
				validityEndDate: data.validityEndDate
					? new Date(data.validityEndDate.toString())
					: undefined,
			},
			files,
		};
	};

	const onSubmit = async (data: PartnershipRequestAnalysisFormData) => {
		if (!data.userId) {
			toast.error("Impossible de sauvegarder", {
				description:
					"Aucun utilisateur n'est associé à cette société. Veuillez d'abord créer un utilisateur.",
			});
			return;
		}
		const dataSubmit = buildFinalizeRequestData({
			...data,
			userId: data.userId,
		});
		saveMutate(dataSubmit);
	};

	const onFinalSubmit = async (data: PartnershipRequestAnalysisFormData) => {
		if (!data.userId) {
			toast.error("Impossible de finaliser", {
				description:
					"Aucun utilisateur n'est associé à cette société. Veuillez d'abord créer un utilisateur.",
			});
			return;
		}
		const dataSubmit = buildFinalizeRequestData({
			...data,
			userId: data.userId,
		});
		finalMutate(dataSubmit);
	};

	return (
		<FileWithMetadataPreviewWrapper>
			<div className="p-4 md:p-6">
				<div className="w-full h-full flex flex-col">
					<div className="mb-2">
						<Button
							variant="ghost"
							className="text-text-main-blue"
							onClick={() =>
								navigate(`${PAGES.BROKERS.show.withParams(id)}?tab=convention`)
							}
						>
							<ArrowLeft /> Retour
						</Button>
					</div>
					<div className="flex items-center justify-between mb-4">
						<h1 className="text-2xl font-semibold text-foreground">
							Analyse demande de partenariat
						</h1>
					</div>

					<FormProvider {...form} key={id}>
						<form
							onSubmit={handleSubmit(onFinalSubmit)}
							className="flex-1 overflow-y-auto pb-32"
						>
							<div className="space-y-6">
								{/* État de validation global */}
								<ValidationStatus
									allDocuments={allWatchedDocs}
									canValidateByCategory={canValidateByCategory}
								/>

								<Separator />

								<div className="text-text-main-blue font-semibold">
									INFORMATIONS SOCIETE
								</div>
								<div
									className={cn(
										"flex flex-col gap-6",
										!isPanelOpen && "xl:flex-row",
									)}
								>
									<SocietySection
										control={control}
										watch={watch}
										isPanelOpen={isPanelOpen}
									/>
									<div className={!isPanelOpen ? "xl:w-1/3" : ""}>
										<DocumentSection
											title="KBIS"
											documents={watchedCompanyDocs}
											documentType="KBIS"
											fieldName="companyDocuments"
											getValues={getValues}
											setValue={setValue}
											societyId={Number(id)}
										/>
									</div>
								</div>

								<Separator />

								<div className="text-text-main-blue font-semibold">
									INFORMATIONS UTILISATEUR
								</div>
								<div
									className={cn(
										"flex flex-col gap-6",
										!isPanelOpen && "xl:flex-row",
									)}
								>
									<UserSection
										control={control}
										errors={errors}
										isPanelOpen={isPanelOpen}
									/>
									<div
										className={cn(
											"flex flex-col gap-6",
											!isPanelOpen && "xl:w-1/3",
										)}
									>
										<DocumentSection
											title="Pièces d'identité"
											documents={watchedUserDocs}
											documentType="IDENTITY"
											fieldName="userDocuments"
											getValues={getValues}
											setValue={setValue}
											societyId={Number(id)}
										/>

										<div>
											<DocumentSection
												title="LCB-FT"
												documents={watchedLcbftDocs}
												documentType="LCB"
												fieldName="lcbftDocuments"
												getValues={getValues}
												setValue={setValue}
												societyId={Number(id)}
											/>
											{errors.lcbftDocuments && (
												<div className="text-red-500 text-sm mt-2">
													{errors.lcbftDocuments.message}
												</div>
											)}
										</div>
									</div>
								</div>

								<Separator />
								<div className="text-text-main-blue font-semibold">
									Informations bancaires
								</div>
								<div>
									<div
										className={cn(
											"flex flex-col gap-6",
											!isPanelOpen && "xl:flex-row",
										)}
									>
										<div className={!isPanelOpen ? "xl:w-2/3" : ""}>
											<Card className="p-6 flex flex-col gap-4 overflow-auto">
												<ControlledInput
													label="IBAN"
													control={control}
													name="iban"
													error={errors.iban?.message?.toString()}
													placeholder="FR76 3000 1007 9412 3456 7890 185"
													maxLength={34}
													onChange={(e) => {
														const cleaned = e.target.value
															.replace(/\s/g, "")
															.toUpperCase();
														setValue("iban", cleaned);
													}}
													onBlur={(e) => {
														const cleaned = e.target.value
															.replace(/\s/g, "")
															.toUpperCase();
														const formatted =
															cleaned.match(/.{1,4}/g)?.join(" ") || cleaned;
														setValue("iban", formatted);
													}}
												/>
												<ControlledInput
													label="BIC/SWIFT"
													control={control}
													name="bic"
													error={errors.bic?.message?.toString()}
												/>
											</Card>
										</div>
										<div className={!isPanelOpen ? "xl:w-1/3" : ""}>
											<DocumentSection
												title="RIB"
												documents={watchedBankDocs}
												documentType="RIB"
												fieldName="bankDocuments"
												getValues={getValues}
												setValue={setValue}
												societyId={Number(id)}
											/>
										</div>
									</div>
								</div>

								<Separator />
								<div className="text-text-main-blue font-semibold">
									Informations RC Professionnelle
								</div>
								<div>
									<div
										className={cn(
											"flex flex-col gap-6",
											!isPanelOpen && "xl:flex-row",
										)}
									>
										<div className={!isPanelOpen ? "xl:w-2/3" : ""}>
											<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
												<div className="flex flex-col gap-4">
													<ControlledInput
														label="Assureur"
														control={control}
														name="insurer"
														error={errors.insurer?.message?.toString()}
													/>
													<div
														className={cn(
															"flex flex-col gap-4",
															!isPanelOpen && "md:flex-row",
														)}
													>
														<div className="flex flex-col items-start gap-3 w-full">
															<div className="text-text-secondary-blue">
																Date de début de validité
															</div>
															<ControlledDateInput
																control={control}
																name="validityStartDate"
																error={errors.validityStartDate?.message?.toString()}
															/>
														</div>
														<div className="flex flex-col items-start gap-3 w-full">
															<div className="text-text-secondary-blue">
																Date de fin de validité
															</div>
															<ControlledDateInput
																control={control}
																name="validityEndDate"
																error={errors.validityEndDate?.message?.toString()}
															/>
														</div>
													</div>
												</div>
											</Card>
										</div>
										<div className={!isPanelOpen ? "xl:w-1/3" : ""}>
											<DocumentSection
												title="RC Professionnelle"
												documents={watchedRcDocs}
												documentType="RC"
												fieldName="rcDocuments"
												getValues={getValues}
												setValue={setValue}
												societyId={Number(id)}
											/>
										</div>
									</div>
								</div>

								<Separator />
								<div className="text-text-main-blue font-semibold">
									PRODUITS ACCESSIBLES
								</div>
								<div className="w-full">
									<Card className="p-6 overflow-auto">
										<DataTable
											columns={accessibleProductsColumns}
											data={watch("accessibleProducts")}
											noResultsText="Aucun produit accessible"
											icon={FileWarning}
											variant="gray-no-pagination"
										/>
									</Card>
								</div>

								<Separator />

								<div className="text-text-main-blue font-semibold">
									COMMENT DUNE A ÉTÉ CONNU
								</div>
								<div
									className={cn(
										"flex flex-col gap-6",
										!isPanelOpen && "xl:flex-row",
									)}
								>
									<div className={!isPanelOpen ? "xl:w-full" : "w-full"}>
										<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
											<div
												className={cn(
													"flex flex-col gap-4",
													!isPanelOpen && "md:flex-row",
												)}
											>
												<ControlledDropdown
													name="howDidYouKnowDune"
													control={control}
													options={howDidYouKnowDuneChoice}
													label="Comment nous avez-vous connu ?"
													error={errors.howDidYouKnowDune?.message?.toString()}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<ControlledCheckbox
													control={control}
													name="newsletter"
													id="newsletter"
													label="Souscrire à la newsletter Dune"
												/>
											</div>
										</Card>
									</div>
								</div>

								{watch("isSignatory") === "false" && (
									<>
										<Separator />
										<div className="text-text-main-blue font-semibold">
											SIGNATAIRE HABILITÉ
										</div>
										<div
											className={cn(
												"flex flex-col gap-6",
												!isPanelOpen && "xl:flex-row",
											)}
										>
											<div className={!isPanelOpen ? "xl:w-full" : "w-full"}>
												<Card className="p-6 flex flex-col gap-4 overflow-auto text-text-secondary-blue">
													<div
														className={cn(
															"flex flex-col gap-4",
															!isPanelOpen && "md:flex-row",
														)}
													>
														<div className="flex flex-col items-start gap-3 w-full">
															<ControlledInput
																label="Nom"
																control={control}
																name="lastnameSignatory"
																error={errors.lastnameSignatory?.message?.toString()}
															/>
														</div>
														<div className="flex flex-col items-start gap-3 w-full">
															<ControlledInput
																label="Prénom"
																control={control}
																name="firstnameSignatory"
																error={errors.firstnameSignatory?.message?.toString()}
															/>
														</div>
													</div>

													<div
														className={cn(
															"flex flex-col gap-4",
															!isPanelOpen && "md:flex-row",
														)}
													>
														<ControlledInput
															label="Email"
															control={control}
															name="emailSignatory"
															error={errors.emailSignatory?.message?.toString()}
														/>
														<ControlledDropdown
															name="positionSignatory"
															control={control}
															options={PositionInTheCompanyChoice}
															label="Fonction dans l'entreprise"
															error={errors.positionSignatory?.message?.toString()}
														/>
													</div>
													<div
														className={cn(
															"flex flex-col gap-4",
															!isPanelOpen && "md:flex-row",
														)}
													>
														<ControlledPhoneInput
															control={control}
															name="landlinePhoneSignatory"
															placeholder="Ex : +33 1 23 45 67 89"
															defaultCountry="FR"
															label="Téléphone fixe"
														/>
														<ControlledPhoneInput
															control={control}
															name="phoneSignatory"
															placeholder="Ex : +33 1 23 45 67 89"
															defaultCountry="FR"
															label="Mobile"
														/>
													</div>
												</Card>
											</div>
										</div>
									</>
								)}
							</div>
						</form>
					</FormProvider>
				</div>
			</div>

			<FloatingActionBar
				totalDocuments={totalDocuments}
				analyzedDocuments={analyzedDocuments}
				compliantDocuments={compliantDocuments}
				saveIsPending={saveIsPending}
				finalIsPending={finalIsPending}
				onSave={() => handleSubmit(onSubmit)()}
				onFinalSubmit={() => handleSubmit(onFinalSubmit)()}
				formIsValid={isValid}
				canValidateByCategory={canValidateByCategory}
			/>
		</FileWithMetadataPreviewWrapper>
	);
};
